import type {
  TraceItem,
  TraceItemType,
  TraceStatus,
  WaterfallTimings,
  FilterMode,
  TrajectoryData
} from '@/types/trace';

// Type definitions for step data from API
interface StepData {
  step_id?: string;
  type?: string;
  step_number?: string | number;
  task_id?: string;
  content?: string;
  content_html?: string;
  tool_calls?: ToolCall[];
  tool_name?: string;
  tool_call_id?: string;
  status?: string;
  output?: string;
  is_cache_hit?: boolean;
  timestamp?: string; // ISO timestamp from API
  start_time?: number;
  end_time?: number;
  duration_ms?: number;
  input_tokens?: number;
  output_tokens?: number;
  task_number?: number;
  task_type?: string;
  task_query?: string;
  worker?: string;
  is_synthesis?: boolean;
  synthesizer_node_name?: string;
  // Structured output observation fields
  observation_type?: 'structured_output' | 'structured_output_fallback' | 'structured_output_error';
  structured_output?: unknown;
  reasoning?: string;  // AI's reasoning text (if any) before/alongside structured output
  [key: string]: unknown;
}

/**
 * Parse timestamp string to milliseconds (always as UTC)
 * Handles timestamps with or without Z suffix consistently
 */
function parseTimestamp(timestamp: string | undefined): number | null {
  if (!timestamp) return null;
  // Ensure timestamp is treated as UTC if it doesn't have timezone info
  let normalizedTimestamp = timestamp;
  if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
    normalizedTimestamp = timestamp + 'Z';
  }
  const date = new Date(normalizedTimestamp);
  return isNaN(date.getTime()) ? null : date.getTime();
}

interface ToolCall {
  id?: string;
  name?: string;
  args?: unknown;
  args_is_json?: boolean;
}

interface TaskExecution {
  task_id?: string;
  task_number?: number;
  task_type?: string;
  task_query?: string;
  worker?: string;
  system_prompt?: string;
  start_step?: number;
  end_step?: number;
  result?: string;
}

interface TaskPlanItem {
  task_id?: string;
  query?: string;
  task_type?: string;
  worker?: string;
}

interface OrchestratorTaskPlans {
  orchestrator?: {
    task_plan?: TaskPlanItem[];
  };
  [key: string]: unknown;
}

/**
 * Get icon for trace item type
 */
export function getTraceIcon(type: TraceItemType): string {
  const icons: Record<TraceItemType, string> = {
    setup: 'Settings',
    task: 'ListTodo',
    tool_call: 'Wrench',
    tool_result: 'CheckCircle',
    observation: 'Eye',
    thinking: 'Brain',
    final_output: 'FileOutput',
    synthesis: 'GitMerge',
  };
  return icons[type] || 'Circle';
}

/**
 * Get color for trace item type (for waterfall view)
 */
export function getTraceColor(type: TraceItemType): string {
  const colors: Record<TraceItemType, string> = {
    setup: 'bg-slate-500',
    task: 'bg-blue-500',
    tool_call: 'bg-amber-500',
    tool_result: 'bg-green-500',
    observation: 'bg-purple-500',
    thinking: 'bg-pink-500',
    final_output: 'bg-emerald-500',
    synthesis: 'bg-cyan-500',
  };
  return colors[type] || 'bg-gray-500';
}

/**
 * Get status from step data
 */
function getStepStatus(step: StepData): TraceStatus {
  if (step.status === 'error') return 'error';
  if (step.status === 'warning') return 'warning';
  if (step.status === 'running' || step.status === 'pending') return 'running';
  return 'success';
}

/**
 * Map step type to TraceItemType
 */
function mapStepType(stepType: string | undefined): TraceItemType {
  const typeMap: Record<string, TraceItemType> = {
    'tool_call': 'tool_call',
    'tool_result': 'tool_result',
    'observation': 'observation',
    'thinking': 'thinking',
    'final_output': 'final_output',
    'task_header': 'task',
    'synthesis_header': 'synthesis',
  };
  return typeMap[stepType || ''] || 'observation';
}

/**
 * Create a setup trace item
 */
function createSetupTraceItem(data: TrajectoryData): TraceItem {
  return {
    id: 'setup',
    parentId: null,
    type: 'setup',
    name: 'Setup',
    displayName: 'Setup & Configuration',
    startTime: data.timestamp ? new Date(data.timestamp).getTime() : null,
    endTime: null,
    duration: null,
    tokenCount: { input: 0, output: 0, total: 0 },
    cost: null,
    status: 'success',
    input: {
      system_prompt: data.system_prompt,
      input_messages: data.input_messages,
    },
    output: {
      available_tools: data.available_tools,
      graph_config: data.graph_config,
    },
    metadata: {
      configured_skills: data.configured_skills,
      loaded_skills: data.loaded_skills,
    },
    depth: 0,
    children: [],
  };
}

/**
 * Create a trace item from a step
 */
function createTraceItemFromStep(
  step: StepData,
  index: number,
  taskMap: Map<string, TraceItem>
): TraceItem {
  const stepId = step.step_id || `step-${index}`;
  const stepType = mapStepType(step.type);

  // Determine display name
  let displayName = step.type || 'Step';
  if (step.type === 'tool_call' && step.tool_calls?.[0]) {
    displayName = step.tool_calls[0].name || 'Tool Call';
  } else if (step.type === 'task_header') {
    displayName = `Task ${step.task_number}: ${step.task_type || 'Task'}`;
  } else if (step.type === 'synthesis_header') {
    displayName = 'Synthesis';
  } else if (step.type === 'observation' && step.observation_type) {
    // Special display names for structured output observations
    if (step.observation_type === 'structured_output') {
      displayName = 'Structured Output';
    } else if (step.observation_type === 'structured_output_fallback') {
      displayName = 'Structured Output (Fallback)';
    } else if (step.observation_type === 'structured_output_error') {
      displayName = 'Structured Output Error';
    }
  }

  // Determine parent
  let parentId: string | null = null;
  if (step.task_id && taskMap.has(step.task_id)) {
    parentId = step.task_id;
  } else if (step.is_synthesis) {
    parentId = 'synthesis';
  }

  // Parse timing - prefer explicit start_time, fall back to timestamp
  const startTime = step.start_time ?? parseTimestamp(step.timestamp);

  const item: TraceItem = {
    id: stepId,
    parentId,
    type: stepType,
    name: step.type || 'step',
    displayName,
    startTime,
    endTime: step.end_time || null,
    duration: step.duration_ms || null,
    tokenCount: {
      input: step.input_tokens || 0,
      output: step.output_tokens || 0,
      total: (step.input_tokens || 0) + (step.output_tokens || 0),
    },
    cost: null,
    status: getStepStatus(step),
    // For observation steps, content is OUTPUT (AI's response), not input
    input: step.type === 'observation' ? undefined : (step.content || step.tool_calls?.[0]?.args),
    output: step.type === 'observation' ? step.content : step.output,
    metadata: {},
    depth: parentId ? 1 : 0,
    children: [],
    originalStep: step,
  };

  // Add tool-specific fields
  if (step.type === 'tool_call' && step.tool_calls?.[0]) {
    item.toolName = step.tool_calls[0].name;
    item.toolArgs = step.tool_calls[0].args;
  }

  if (step.type === 'tool_result') {
    item.toolResult = step.output;
    item.isCacheHit = step.is_cache_hit;
  }

  // Add structured output observation fields
  if (step.observation_type) {
    item.observationType = step.observation_type;
  }
  if (step.structured_output) {
    item.structuredOutput = step.structured_output;
  }
  if (step.reasoning) {
    item.reasoning = step.reasoning;
  }

  // Add task-specific fields
  if (step.type === 'task_header') {
    item.taskId = step.task_id;
    item.taskNumber = step.task_number;
    item.taskType = step.task_type;
    item.taskQuery = step.task_query;
    item.worker = step.worker;
  }

  return item;
}

/**
 * Transform API trajectory data to normalized TraceItem tree
 */
export function transformToTraceItems(data: TrajectoryData): TraceItem[] {
  const traceItems: TraceItem[] = [];
  const itemsMap = new Map<string, TraceItem>();
  const taskMap = new Map<string, TraceItem>();

  // 1. Add Setup as root item
  const setupItem = createSetupTraceItem(data);
  traceItems.push(setupItem);
  itemsMap.set(setupItem.id, setupItem);

  // Build a map of task_id -> query from orchestrator_task_plans
  const taskQueryMap = new Map<string, string>();
  const orchestratorPlans = data.orchestrator_task_plans as OrchestratorTaskPlans | undefined;
  if (orchestratorPlans?.orchestrator?.task_plan) {
    orchestratorPlans.orchestrator.task_plan.forEach((plan) => {
      if (plan.task_id && plan.query) {
        taskQueryMap.set(plan.task_id, plan.query);
      }
    });
  }

  // 2. Create task items first (for hierarchy)
  const taskExecutions = (data.task_executions || []) as TaskExecution[];
  taskExecutions.forEach((task) => {
    if (!task.task_id) return;

    // Get the task query - prefer from orchestrator_task_plans, fall back to task_execution
    const taskQuery = taskQueryMap.get(task.task_id) || task.task_query;

    // Build a good display name from available data
    // Convert snake_case to Title Case
    const formatTaskType = (type: string) => {
      return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };
    const taskLabel = task.task_type
      ? formatTaskType(task.task_type)
      : taskQuery?.slice(0, 30) || 'Task';
    const taskNum = task.task_number != null ? `#${task.task_number} ` : '';

    const taskItem: TraceItem = {
      id: task.task_id,
      parentId: null,
      type: 'task',
      name: 'task',
      displayName: `${taskNum}${taskLabel}`,
      startTime: null,
      endTime: null,
      duration: null,
      tokenCount: { input: 0, output: 0, total: 0 },
      cost: null,
      status: 'success',
      input: taskQuery,
      output: task.result,
      metadata: { worker: task.worker },
      taskId: task.task_id,
      taskNumber: task.task_number,
      taskType: task.task_type,
      taskQuery: taskQuery,
      worker: task.worker,
      systemPrompt: task.system_prompt,
      depth: 0,
      children: [],
    };

    taskMap.set(task.task_id, taskItem);
    itemsMap.set(task.task_id, taskItem);
    traceItems.push(taskItem);
  });

  // 3. Process steps - use two-pass approach for tool_call/tool_result linking
  // Steps may not be in chronological order due to parallel task execution,
  // so tool_results might appear before their corresponding tool_calls in the array
  const steps = (data.steps || []) as StepData[];
  const toolCallMap = new Map<string, TraceItem>(); // Map tool_call ID to its TraceItem

  // First pass: create tool_call items and build the map
  // When a step contains multiple tool_calls (parallel execution), create SEPARATE items for each
  steps.forEach((step, index) => {
    if (step.type === 'tool_call') {
      const toolCalls = step.tool_calls || [];

      // Create a separate TraceItem for EACH tool call in the bundle
      toolCalls.forEach((toolCall, toolIndex) => {
        const itemId = toolCall.id || `step-${index}-tool-${toolIndex}`;

        // Determine parent based on task
        let parentId: string | null = null;
        if (step.task_id && taskMap.has(step.task_id)) {
          parentId = step.task_id;
        } else if (step.is_synthesis) {
          parentId = 'synthesis';
        }

        // Parse timing
        const startTime = step.start_time ?? parseTimestamp(step.timestamp);

        const item: TraceItem = {
          id: itemId,
          parentId,
          type: 'tool_call',
          name: 'tool_call',
          displayName: toolCall.name || 'Tool Call',
          startTime,
          endTime: step.end_time || null,
          duration: step.duration_ms || null,
          tokenCount: {
            input: step.input_tokens || 0,
            output: step.output_tokens || 0,
            total: (step.input_tokens || 0) + (step.output_tokens || 0),
          },
          cost: null,
          status: getStepStatus(step),
          input: toolCall.args,
          output: undefined,
          metadata: {},
          depth: parentId ? 1 : 0,
          children: [],
          toolName: toolCall.name,
          toolArgs: toolCall.args,
          originalStep: step,
        };

        itemsMap.set(item.id, item);

        // Track by tool_call ID for linking with tool_results
        if (toolCall.id && !toolCallMap.has(toolCall.id)) {
          toolCallMap.set(toolCall.id, item);
        }

        // Determine parent and add to tree
        let parentItem: TraceItem | undefined;
        if (item.parentId && itemsMap.has(item.parentId)) {
          parentItem = itemsMap.get(item.parentId);
        }
        if (parentItem) {
          parentItem.children.push(item);
          item.depth = parentItem.depth + 1;
        } else {
          traceItems.push(item);
        }
      });
    }
  });

  // Second pass: process remaining steps (tool_results merge into tool_calls, others are added)
  steps.forEach((step, index) => {
    // Skip task_header and synthesis_header as we already have task items
    if (step.type === 'task_header' || step.type === 'synthesis_header') {
      return;
    }

    // Skip tool_calls - already processed in first pass
    if (step.type === 'tool_call') {
      return;
    }

    // For tool_result, attach output to its parent tool_call instead of creating separate item
    if (step.type === 'tool_result' && step.tool_call_id) {
      const parentToolCall = toolCallMap.get(step.tool_call_id);
      if (parentToolCall) {
        // Merge tool_result data into the tool_call item
        parentToolCall.output = step.output;
        parentToolCall.toolResult = step.output;
        parentToolCall.isCacheHit = step.is_cache_hit;
        // Update endTime if available
        const resultTime = parseTimestamp(step.timestamp);
        if (resultTime !== null) {
          parentToolCall.endTime = resultTime;
          if (parentToolCall.startTime !== null) {
            parentToolCall.duration = resultTime - parentToolCall.startTime;
          }
        }
        // Remove from map so duplicate IDs create standalone items
        // (handles eval runs where same agent runs multiple times with identical tool_call_ids)
        toolCallMap.delete(step.tool_call_id);
        return; // Don't create a separate item for tool_result
      }
    }

    const item = createTraceItemFromStep(step, index, taskMap);
    itemsMap.set(item.id, item);

    // Determine parent based on task
    let parentItem: TraceItem | undefined;
    if (item.parentId && itemsMap.has(item.parentId)) {
      parentItem = itemsMap.get(item.parentId);
    }

    // Add to parent's children or to root
    if (parentItem) {
      parentItem.children.push(item);
      item.depth = parentItem.depth + 1;
    } else {
      traceItems.push(item);
    }
  });

  // 4. Calculate cumulative metrics
  calculateCumulativeMetrics(traceItems);

  return traceItems;
}

/**
 * Calculate cumulative metrics for tree items
 */
function calculateCumulativeMetrics(items: TraceItem[]): void {
  items.forEach(item => {
    if (item.children.length > 0) {
      calculateCumulativeMetrics(item.children);

      // Sum up children metrics and calculate timing bounds
      let minStartTime: number | null = null;
      let maxEndTime: number | null = null;

      item.children.forEach(child => {
        item.tokenCount.input += child.tokenCount.input;
        item.tokenCount.output += child.tokenCount.output;
        item.tokenCount.total += child.tokenCount.total;

        // Track earliest start time
        if (child.startTime !== null) {
          if (minStartTime === null || child.startTime < minStartTime) {
            minStartTime = child.startTime;
          }
        }

        // Track latest end time
        if (child.endTime !== null) {
          if (maxEndTime === null || child.endTime > maxEndTime) {
            maxEndTime = child.endTime;
          }
        }
      });

      // Set parent timing from children bounds
      if (item.startTime === null && minStartTime !== null) {
        item.startTime = minStartTime;
      }
      if (item.endTime === null && maxEndTime !== null) {
        item.endTime = maxEndTime;
      }

      // Calculate duration from start/end if not set
      if (item.duration === null && item.startTime !== null && item.endTime !== null) {
        item.duration = item.endTime - item.startTime;
      }
    }
  });
}

/**
 * Calculate timing information for waterfall view
 */
export function calculateWaterfallTimings(traceItems: TraceItem[]): WaterfallTimings {
  let minTime = Infinity;
  let maxTime = 0;

  const processItem = (item: TraceItem) => {
    if (item.startTime !== null) {
      if (item.startTime < minTime) {
        minTime = item.startTime;
      }
      // Use startTime + duration for maxTime, or just startTime if no duration
      const itemEnd = item.endTime ?? (item.duration ? item.startTime + item.duration : item.startTime);
      if (itemEnd > maxTime) {
        maxTime = itemEnd;
      }
    }
    item.children.forEach(processItem);
  };

  traceItems.forEach(processItem);

  // If no timing data, use defaults
  if (minTime === Infinity) minTime = 0;
  if (maxTime <= minTime) maxTime = minTime + 1000;

  return {
    timelineStart: minTime,
    timelineEnd: maxTime,
    totalDuration: maxTime - minTime,
  };
}

/**
 * Flatten trace items for waterfall view
 */
export function flattenTraceItems(items: TraceItem[]): TraceItem[] {
  const result: TraceItem[] = [];

  const processItem = (item: TraceItem) => {
    result.push(item);
    item.children.forEach(processItem);
  };

  items.forEach(processItem);
  return result;
}

/**
 * Get all node IDs from trace items (for expand all)
 */
export function getAllNodeIds(items: TraceItem[]): string[] {
  const ids: string[] = [];

  const processItem = (item: TraceItem) => {
    if (item.children.length > 0) {
      ids.push(item.id);
      item.children.forEach(processItem);
    }
  };

  items.forEach(processItem);
  return ids;
}

/**
 * Find trace item by ID
 */
export function findTraceItemById(items: TraceItem[], id: string): TraceItem | null {
  for (const item of items) {
    if (item.id === id) return item;
    const found = findTraceItemById(item.children, id);
    if (found) return found;
  }
  return null;
}

/**
 * Get path to a trace item (for highlighting)
 */
export function getPathToItem(items: TraceItem[], targetId: string): string[] {
  const path: string[] = [];

  const findPath = (item: TraceItem, currentPath: string[]): boolean => {
    const newPath = [...currentPath, item.id];

    if (item.id === targetId) {
      path.push(...newPath);
      return true;
    }

    for (const child of item.children) {
      if (findPath(child, newPath)) {
        return true;
      }
    }

    return false;
  };

  for (const item of items) {
    if (findPath(item, [])) {
      break;
    }
  }

  return path;
}

/**
 * Filter trace items based on filter mode
 */
export function filterTraceItems(
  items: TraceItem[],
  filterMode: FilterMode,
  searchQuery = ''
): TraceItem[] {
  if (filterMode === 'show_all' && !searchQuery) {
    return items;
  }

  const filterItem = (item: TraceItem): TraceItem | null => {
    // Check if matches search query
    const matchesSearch = !searchQuery ||
      item.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase());

    // Check filter mode
    let matchesFilter = true;
    if (filterMode === 'most_relevant') {
      // Show only important items: tasks, tool calls, final output
      matchesFilter = ['task', 'tool_call', 'final_output', 'setup'].includes(item.type);
    } else if (filterMode === 'filtered_only') {
      // Show only items that match the search
      matchesFilter = matchesSearch;
    }

    // Filter children
    const filteredChildren = item.children
      .map(filterItem)
      .filter((child): child is TraceItem => child !== null);

    // Include if matches or has matching children
    if ((matchesFilter && matchesSearch) || filteredChildren.length > 0) {
      return {
        ...item,
        children: filteredChildren,
      };
    }

    return null;
  };

  return items
    .map(filterItem)
    .filter((item): item is TraceItem => item !== null);
}

/**
 * Format duration for display
 */
export function formatDuration(ms: number | null): string {
  if (ms === null) return '-';

  if (ms < 1000) {
    return `${ms.toFixed(0)}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(2)}s`;
  } else {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(1);
    return `${minutes}m ${seconds}s`;
  }
}

/**
 * Format token count for display
 */
export function formatTokens(count: number): string {
  if (count === 0) return '-';
  if (count < 1000) return count.toString();
  return `${(count / 1000).toFixed(1)}k`;
}
